/* eslint-disable react-hooks/rules-of-hooks */
import { PageHeader } from "../../../bits/PageHeader/PageHeader";
import * as React from "react";
import styles from "./ManageLocations.module.scss";
import { Button } from "../../../bits/Button/Button";
import { IACLEmail } from "intake_shared/src/types";
import { CacheContext } from "../../../../../contexts/CacheContext";
import {
    addOfficeManager,
    removeOfficeManager,
    updateLocationAddressCache,
    updateOfficeMangerCache,
    updateOfficesCache,
    addOffice,
    editOfficeName,
    deleteOffice,
} from "../../../../../store/cache/location";
import AddNewLocation from "./AddNewLocation/AddNewLocation";
import { hasOfficeAdminBetaAccess } from "../../../../../appSettings";
import { AclContext } from "../../../../../contexts/AclContext";
import { useToasts } from "react-toast-notifications";
import ChevronUpIcon from "../../../icons/ChevronUp";
import ChevronDownIcon from "../../../icons/ChevronDown";
import { cn } from "../../../../helpers";
import { sortStringsWithNumberEndings } from "../../../../../utils/utils";
import { uniq } from "lodash";
import EditIcon from "../../../icons/Edit";
import TrashIcon from "../../../icons/Trash";
import PlusIcon from "../../../icons/Plus";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { Input } from "../../../bits/Input/Input";
import { Select } from "../../../bits/Select/Select";
import store from "../../../../../store";
import Loader from "../../../bits/Loader/Loader";

type DialogType = "edit-room" | "add-room" | "add-manager";

function LocationBox({
    locationIdx,
    locationName,
    officeNames,
    locationOfficeManager,
    authUserEmail,
    userRoles,
    onUpdate,
    onUpdateOfficeManager,
    locationAddress,
    onUpdateAddress,
}: {
    locationIdx: number;
    locationName: string;
    authUserEmail: string;
    userRoles: IACLEmail[] | undefined;
    officeNames: string[];
    locationOfficeManager: string[];
    onUpdate: () => void;
    onUpdateOfficeManager: () => void;
    locationAddress?: string;
    onUpdateAddress: () => void;
}) {
    const [isOpen, setIsOpen] = React.useState(false);
    const { addToast } = useToasts();

    const [showLoader, setShowLoader] = React.useState(false);
    const [dialog, setDialog] = React.useState<DialogType | null>(null);

    // State for edit room dialog
    const [editingOfficeName, setEditingOfficeName] = React.useState("");
    const [editedOfficeName, setEditedOfficeName] = React.useState("");

    // State for add room dialog
    const [newRoomName, setNewRoomName] = React.useState("");

    // State for add manager dialog
    const [selectedManagers, setSelectedManagers] = React.useState<string[]>([]);

    // State for delete office check
    const [getAllBookingsAndOfficeWindows, setGetAllBookingsAndOfficeWindows] =
        React.useState<any[]>([]);

    const toggleShowContent = () => {
        setIsOpen(!isOpen);
    };

    // Get available office managers for this location
    const officeManagersEmails =
        userRoles
            ?.filter(
                (userRole) => userRole.acl && userRole.acl.office_manager === true
            )
            ?.map((userRole) => userRole.email) || [];

    const officeManagersToAdd = officeManagersEmails.filter(
        (email) => !locationOfficeManager.includes(email)
    );

    const officeManagerOptions = officeManagersToAdd.map((email) => ({
        value: email,
        label: email,
    }));

    // Fetch clinicians data for delete office check
    React.useEffect(() => {
        const fetchClinicians = async () => {
            try {
                const clinicians = await store.searchClincians({
                    available_days_only: false,
                    first_name: "",
                    age_categories: [],
                    methodology: {},
                    experience_level: {},
                    search_locations: [],
                    search_credentials: [],
                    experience_level_filter: {},
                    show_only_available: false,
                    available_services: [],
                    priority_marked: false,
                });

                const selectedFieldsArray = clinicians.map((clinician: any) => {
                    const {
                        confirmed,
                        held,
                        office_windows,
                        available,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        ...selectedFields
                    } = clinician;
                    return {
                        confirmed,
                        held,
                        office_windows,
                        available,
                    };
                });
                setGetAllBookingsAndOfficeWindows(selectedFieldsArray);
            } catch (error) {
                console.error("Error fetching clinicians:", error);
            }
        };

        fetchClinicians();
    }, []);

    async function addNewOfficeManager() {
        try {
            await addOfficeManager(locationName, selectedManagers);
            addToast(`Added office manager(s) to ${locationName}`, {
                appearance: "success",
            });
            setSelectedManagers([]);
            onUpdateOfficeManager();
            setDialog(null);
        } catch (ex) {
            console.error(ex);
            addToast("Error saving office manager", { appearance: "error" });
        }
    }

    async function handleRemoveOfficeManager(officeManagerToRemove: string) {
        try {
            await removeOfficeManager(locationName, officeManagerToRemove);
            addToast(`Office manager removed from ${locationName}`, {
                appearance: "success",
            });
            onUpdateOfficeManager();
        } catch (ex) {
            console.error(ex);
            addToast("Error saving office manager", { appearance: "error" });
        }
    }

    const handleEditOffice = async (
        locationName: string,
        officeName: string,
        newOfficeName: string
    ) => {
        try {
            setShowLoader(true);
            await store.updateClinicianAndClientDocuments(officeName, newOfficeName);
            await editOfficeName(locationName, officeName, newOfficeName);
            addToast(
                `${officeName} has been changed to ${newOfficeName} successfully`,
                {
                    appearance: "success",
                }
            );
            onUpdate();
            setDialog(null);
            setEditingOfficeName("");
            setEditedOfficeName("");
        } catch (ex) {
            console.error(ex);
            addToast("Error saving new office name", { appearance: "error" });
        } finally {
            setShowLoader(false);
        }
    };

    const handleAddRoom = async () => {
        try {
            setShowLoader(true);
            await addOffice(locationName, newRoomName);
            addToast(`Added new room ${newRoomName} to ${locationName}`, {
                appearance: "success",
            });
            setNewRoomName("");
            onUpdate();
            setShowLoader(false);
            setDialog(null);
        } catch (ex) {
            console.error(ex);
            addToast("Error saving new room", { appearance: "error" });
        }
    };

    const handleDeleteOffice = async (locationName: string, officeName: string) => {
        try {
            const isOfficeUsed = getAllBookingsAndOfficeWindows.some((clinician) => {
                // Check if the officeName exists in any of the arrays
                return [...clinician.office_windows, ...clinician.available].some(
                    (booking) =>
                        booking.office === officeName &&
                        booking.location === locationName
                );
            });
            if (isOfficeUsed) {
                addToast(
                    "Office is in use and cannot be deleted. Please remove Office windows and appointments associated with this office before attempting to delete",
                    {
                        appearance: "warning",
                    }
                );
            } else {
                await deleteOffice(locationName, officeName);
                addToast(`${officeName} has been deleted successfully`, {
                    appearance: "success",
                });
                onUpdate();
            }
        } catch (ex) {
            console.error(ex);
            addToast("Error deleting office", { appearance: "error" });
        }
    };

    const openEditRoomDialog = (officeName: string) => {
        setEditingOfficeName(officeName);
        setEditedOfficeName(officeName);
        setDialog("edit-room");
    };

    const openAddRoomDialog = () => {
        setNewRoomName("");
        setDialog("add-room");
    };

    const openAddManagerDialog = () => {
        setSelectedManagers([]);
        setDialog("add-manager");
    };

    return (
        <>
            <Loader
                show={showLoader}
                loadingText="Please wait while we process your request. This may take a moment, especially for busy office locations with many appointments."
            />
            <div className={styles.locationBox} role="rowgroup">
                <div className={styles.accordionHeader} role="row">
                    <div className={styles.accordionHeaderLeft} role="presentation">
                        <div
                            className={styles.locationDetails}
                            role="cell"
                            aria-labelledby="locations-name-header"
                        >
                            <span className={styles.locationDetailsValue}>
                                {locationName}
                            </span>
                        </div>
                        <div
                            className={styles.locationDetails}
                            role="cell"
                            aria-labelledby="locations-address-header"
                        >
                            <span className={styles.locationDetailsValue}>
                                {locationAddress || "--"}
                            </span>
                        </div>
                        <div
                            className={styles.locationDetails}
                            role="cell"
                            aria-labelledby="locations-rooms-header"
                        >
                            <span className={styles.locationDetailsValue}>
                                {officeNames.length}
                            </span>
                        </div>
                    </div>
                    <div
                        className={styles.accordionHeaderButton}
                        role="cell"
                        aria-labelledby="locations-actions-header"
                    >
                        <Button
                            variant="normal"
                            size="icon"
                            onClick={toggleShowContent}
                            title={isOpen ? "Hide rooms" : "Show rooms"}
                            aria-label={isOpen ? "Hide rooms" : "Show rooms"}
                            aria-expanded={isOpen}
                        >
                            {isOpen ? (
                                <ChevronUpIcon stroke="var(--color-gray)" />
                            ) : (
                                <ChevronDownIcon stroke="var(--color-gray)" />
                            )}
                        </Button>
                    </div>
                </div>
                <div
                    className={cn(styles.accordionContent, isOpen && styles.open)}
                    role="row"
                >
                    <div
                        className={cn(styles.contentInner)}
                        role="cell"
                        aria-colspan={4}
                    >
                        <div className={styles.locationDetails}>
                            <p className={styles.locationDetailsLabel}>Rooms</p>
                            <div className={styles.detailsValueContainer}>
                                {officeNames.map((officeName, index) => (
                                    <div key={index} className={styles.detailsValue}>
                                        {officeName}
                                        <div className={styles.detailsActions}>
                                            <Button
                                                variant="normal"
                                                className={styles.detailsButton}
                                                onClick={() =>
                                                    openEditRoomDialog(officeName)
                                                }
                                            >
                                                <EditIcon stroke="var(--color-gray)" />
                                            </Button>
                                            <Button
                                                variant="normal"
                                                className={styles.detailsButton}
                                                onClick={() =>
                                                    handleDeleteOffice(
                                                        locationName,
                                                        officeName
                                                    )
                                                }
                                            >
                                                <TrashIcon
                                                    stroke="var(--color-gray)"
                                                    width="14"
                                                    height="14"
                                                    strokeWidth="1.5"
                                                />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <Button
                                    variant="normal"
                                    className={styles.addOfficeButton}
                                    iconLeft={<PlusIcon />}
                                    onClick={openAddRoomDialog}
                                >
                                    Add room
                                </Button>
                            </div>
                        </div>
                        <div className={styles.locationDetails}>
                            <p className={styles.locationDetailsLabel}>
                                OFFICE MANAGERS
                            </p>
                            <div className={styles.detailsValueContainer}>
                                {locationOfficeManager &&
                                    locationOfficeManager?.map((manager, index) => (
                                        <div
                                            key={index}
                                            className={styles.detailsValue}
                                        >
                                            {manager}
                                            <div className={styles.detailsActions}>
                                                <Button
                                                    variant="normal"
                                                    className={styles.detailsButton}
                                                    onClick={() =>
                                                        handleRemoveOfficeManager(
                                                            manager
                                                        )
                                                    }
                                                >
                                                    <TrashIcon
                                                        stroke="var(--color-gray)"
                                                        width="14"
                                                        height="14"
                                                        strokeWidth="1.5"
                                                    />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                            <div>
                                <Button
                                    variant="normal"
                                    className={styles.addOfficeButton}
                                    iconLeft={<PlusIcon />}
                                    onClick={openAddManagerDialog}
                                >
                                    Add office manager
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Room Dialog */}
            <Dialog
                open={dialog === "edit-room"}
                onClose={() => setDialog(null)}
                title="Edit Room Name"
                onSave={() =>
                    handleEditOffice(
                        locationName,
                        editingOfficeName,
                        editedOfficeName
                    )
                }
                onCancel={() => setDialog(null)}
                isSubmitBtnDisabled={
                    !editedOfficeName.trim() ||
                    editedOfficeName === editingOfficeName
                }
            >
                <div style={{ padding: "16px 0" }}>
                    <Input
                        label="Room Name"
                        value={editedOfficeName}
                        onChange={(e) => setEditedOfficeName(e.target.value)}
                        placeholder="Enter room name"
                        isRequired
                    />
                </div>
            </Dialog>

            {/* Add Room Dialog */}
            <Dialog
                open={dialog === "add-room"}
                onClose={() => setDialog(null)}
                title="Add New Room"
                onSave={handleAddRoom}
                onCancel={() => setDialog(null)}
                isSubmitBtnDisabled={!newRoomName.trim()}
            >
                <div style={{ padding: "16px 0" }}>
                    <Input
                        label="Room Name"
                        value={newRoomName}
                        onChange={(e) => setNewRoomName(e.target.value)}
                        placeholder="Enter room name"
                        isRequired
                    />
                </div>
            </Dialog>

            {/* Add Manager Dialog */}
            <Dialog
                open={dialog === "add-manager"}
                onClose={() => setDialog(null)}
                title="Add Office Manager"
                onSave={addNewOfficeManager}
                onCancel={() => setDialog(null)}
                isSubmitBtnDisabled={selectedManagers.length === 0}
            >
                <div style={{ padding: "16px 0" }}>
                    <Select
                        label="Office Managers (You can select multiple office managers)"
                        placeholder="Select office managers"
                        options={officeManagerOptions}
                        value={officeManagerOptions.filter((option) =>
                            selectedManagers.includes(option.value)
                        )}
                        onChange={(selectedOptions) => {
                            if (Array.isArray(selectedOptions)) {
                                setSelectedManagers(
                                    selectedOptions.map((option) => option.value)
                                );
                            } else {
                                setSelectedManagers([]);
                            }
                        }}
                        isMulti
                        multiHasValues={selectedManagers.length > 0}
                        name="officeManagers"
                        isLongListInDialog
                    />
                </div>
            </Dialog>
        </>
    );
}

export default function ManageLocations() {
    const authUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const olc = React.useContext(CacheContext);

    const [isAddLocationOpen, setIsAddLocationOpen] = React.useState(false);

    const officeManagersEmails =
        olc &&
        olc?.allUserRoles
            ?.filter(
                (userRole) => userRole.acl && userRole.acl.office_manager === true
            )
            ?.map((userRole) => userRole.email);
    if (!olc) {
        return <></>;
    }

    async function updateOfficesProvider() {
        const updatedOffices = await updateOfficesCache();
        if (updatedOffices) {
            olc?.updateOffices(updatedOffices);
        }
    }
    async function updateOfficeManagerProvider() {
        const updatedOfficeManager = await updateOfficeMangerCache();
        if (updatedOfficeManager) {
            olc?.updateOfficeManager(updatedOfficeManager);
        }
    }
    async function updateLocationAddressProvider() {
        const updatedLocationAddress = await updateLocationAddressCache();
        if (updatedLocationAddress) {
            olc?.updateLocationAddress(updatedLocationAddress);
        }
    }

    const onAddLocationSuccess = async () => {
        setIsAddLocationOpen(false);
        await updateOfficesProvider();
        await updateOfficeManagerProvider();
        await updateLocationAddressProvider();
    };

    return (
        <div className={styles.locationsWrapper}>
            <PageHeader
                title="Locations"
                meta={`${Object.keys(olc.offices || {}).length} locations`}
                actions={
                    <Button onClick={() => setIsAddLocationOpen(true)}>
                        Add Location
                    </Button>
                }
                className={styles.pageHeader}
            />
            <div
                className={styles.locationList}
                role="table"
                aria-label="Practice locations"
            >
                <div className={styles.listHeader} role="row">
                    <span id="locations-name-header" role="columnheader">
                        Location
                    </span>
                    <span id="locations-address-header" role="columnheader">
                        Address
                    </span>
                    <span id="locations-rooms-header" role="columnheader">
                        Rooms
                    </span>
                    <span
                        id="locations-actions-header"
                        role="columnheader"
                        aria-label="Actions"
                    />
                </div>
                {Object.entries(olc.offices || {}).map(
                    ([locationName, officeNames], idx) => {
                        const officeManagerEmails =
                            olc.office_managers && olc.office_managers[locationName]
                                ? Object.values(olc.office_managers[locationName])
                                : [];
                        const locationAddress = olc.location_address?.[locationName];
                        return (
                            <LocationBox
                                key={`location-${idx}`}
                                locationIdx={idx}
                                locationName={locationName}
                                officeNames={sortStringsWithNumberEndings(
                                    uniq(officeNames)
                                )}
                                userRoles={olc.allUserRoles}
                                locationOfficeManager={officeManagerEmails}
                                authUserEmail={authUserEmail as string}
                                onUpdate={updateOfficesProvider}
                                onUpdateOfficeManager={updateOfficeManagerProvider}
                                locationAddress={locationAddress}
                                onUpdateAddress={updateLocationAddressProvider}
                            />
                        );
                    }
                )}
            </div>

            <AddNewLocation
                open={isAddLocationOpen}
                onClose={() => setIsAddLocationOpen(false)}
                onSuccess={onAddLocationSuccess}
                officeManagersEmails={officeManagersEmails}
                hasOfficeAdminBetaAccess={hasOfficeAdminBetaAccess(
                    authUserEmail as string
                )}
            />
        </div>
    );
}
